import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowRightLeft,
  ChevronDown,
  CircleAlert,
  CreditCard,
  Crown,
  Repeat,
  ShieldAlert,
  ShieldCheck,
  Sunrise,
  User,
} from 'lucide-react';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  IdChip,
  Input,
  Pagination,
  Row,
  Select,
  Skeleton,
  StatTile,
  cn,
} from '../components/ui';
import { api, compact } from '../lib/api';
import { TYPING_DEBOUNCE_MS, useDebounced } from '../lib/debounce';
import {
  formatCredits,
  formatDateTime,
  formatMoneyMinor,
  formatNumber,
  formatSpan,
} from '../lib/format';
import { useT } from '../lib/i18n';
import { useOpenRecord } from '../lib/navigation';
import type {
  ConversionStatus,
  ConversionTrailStep,
  ModeConversion,
  ModeConversionDetail,
  ModeConversionSummary,
  Paginated,
  PremiumPaymentProof,
} from '../lib/types';

const WINDOWS = [7, 30, 90] as const;

type Tone = 'neutral' | 'brand' | 'good' | 'warn' | 'danger';

const STATUS_TONES: Record<ConversionStatus, Tone> = {
  pending: 'brand',
  completed: 'good',
  failed: 'danger',
  expired: 'warn',
};

/** The colour of each step's marker on the trail. */
const TRAIL_TONES: Record<ConversionTrailStep, Tone> = {
  opened: 'brand',
  daylights_taken: 'brand',
  daylights_returned: 'warn',
  daylights_granted: 'good',
  daylights_reclaimed: 'warn',
  checkout_started: 'brand',
  card_verified: 'brand',
  payment_captured: 'good',
  payment_failed: 'danger',
  premium_opened: 'good',
  premium_ended: 'neutral',
  completed: 'good',
  failed: 'danger',
  expired: 'warn',
};

const DOT_TONES: Record<Tone, string> = {
  neutral: 'bg-ink-3',
  brand: 'bg-brand',
  good: 'bg-good',
  warn: 'bg-warn',
  danger: 'bg-danger',
};

/**
 * Every switch between reward mode and Premium.
 *
 * Built for the two questions support actually gets: "what happened to my
 * daylights?" and "why was I charged that much?". Each row carries the whole
 * quote the user was shown — not just the outcome — so both are answered
 * without leaving the page. The summary above says whether switching, as a
 * whole, is behaving: how many went through, how much value moved each way,
 * and how many stalled.
 */
export function ConversionsPage() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [days, setDays] = useState<(typeof WINDOWS)[number]>(30);
  const [page, setPage] = useState(1);

  const filters = {
    direction: params.get('direction') ?? undefined,
    status: params.get('status') ?? undefined,
    deviceId: params.get('deviceId') ?? undefined,
  };
  // Typed a character at a time; one request per pause, not per keystroke.
  const deviceId = useDebounced(filters.deviceId, TYPING_DEBOUNCE_MS);

  const set = (key: keyof typeof filters, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setPage(1);
  };

  const query = useMemo(
    () =>
      compact({
        direction: filters.direction,
        status: filters.status,
        deviceId,
        page,
        limit: 25,
      }),
    [filters.direction, filters.status, deviceId, page],
  );

  const summary = useQuery({
    queryKey: ['conversions', 'summary', days],
    queryFn: () =>
      api.get<ModeConversionSummary>('/api/conversions/summary', { days }),
  });

  const conversions = useQuery({
    queryKey: ['conversions', query],
    queryFn: () =>
      api.get<Paginated<ModeConversion>>('/api/conversions', query),
  });

  return (
    <>
      <PageHeader
        title={t('conversions.title')}
        description={t('conversions.description')}
        action={
          <Select
            aria-label={t('conversions.window')}
            value={String(days)}
            onChange={(event) =>
              setDays(Number(event.target.value) as (typeof WINDOWS)[number])
            }
          >
            {WINDOWS.map((option) => (
              <option key={option} value={option}>
                {t('conversions.lastDays', { count: option })}
              </option>
            ))}
          </Select>
        }
      />

      <div className="space-y-4">
        <SummaryTiles summary={summary.data} loading={summary.isPending} />
        {summary.isError && <ErrorNote error={summary.error} />}

        <div className="grid gap-3 rounded-xl border border-line bg-surface p-3.5 sm:grid-cols-3 sm:p-4">
          <Field label={t('conversions.filter.direction')}>
            <Select
              value={filters.direction ?? ''}
              onChange={(event) => set('direction', event.target.value)}
            >
              <option value="">{t('conversions.filter.anyDirection')}</option>
              <option value="reward_to_premium">
                {t('conversions.direction.reward_to_premium')}
              </option>
              <option value="premium_to_reward">
                {t('conversions.direction.premium_to_reward')}
              </option>
            </Select>
          </Field>
          <Field label={t('conversions.filter.status')}>
            <Select
              value={filters.status ?? ''}
              onChange={(event) => set('status', event.target.value)}
            >
              <option value="">{t('conversions.filter.anyStatus')}</option>
              {(Object.keys(STATUS_TONES) as ConversionStatus[]).map((status) => (
                <option key={status} value={status}>
                  {t(`conversions.status.${status}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('common.deviceId')}>
            <Input
              className="font-mono text-xs"
              value={filters.deviceId ?? ''}
              onChange={(event) => set('deviceId', event.target.value.trim())}
              placeholder={t('common.pasteDeviceId')}
            />
          </Field>
        </div>

        {conversions.isError && <ErrorNote error={conversions.error} />}

        <Card>
          {conversions.isPending ? (
            <Skeleton className="h-96" />
          ) : (conversions.data?.items.length ?? 0) === 0 ? (
            <EmptyState
              icon={<Repeat size={28} />}
              title={t('conversions.emptyTitle')}
              description={t('conversions.emptyDescription')}
            />
          ) : (
            <>
              <ul className="divide-y divide-line">
                {conversions.data?.items.map((conversion) => (
                  <ConversionRow key={conversion.id} conversion={conversion} />
                ))}
              </ul>
              <Pagination
                page={conversions.data?.page ?? 1}
                totalPages={conversions.data?.totalPages ?? 1}
                total={conversions.data?.total ?? 0}
                onChange={setPage}
              />
            </>
          )}
        </Card>
      </div>
    </>
  );
}

function SummaryTiles({
  summary,
  loading,
}: {
  summary: ModeConversionSummary | undefined;
  loading: boolean;
}) {
  const t = useT();

  if (loading || !summary) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-24" />
        ))}
      </div>
    );
  }

  const toPremium = summary.counts.reward_to_premium ?? {};
  const toReward = summary.counts.premium_to_reward ?? {};
  const stalled =
    (toPremium.failed ?? 0) +
    (toPremium.expired ?? 0) +
    (toReward.failed ?? 0) +
    (toReward.expired ?? 0);
  const pending = (toPremium.pending ?? 0) + (toReward.pending ?? 0);

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
      <StatTile
        tone="brand"
        icon={<Crown size={14} />}
        label={t('conversions.tile.toPremium')}
        value={formatNumber(toPremium.completed ?? 0)}
        caption={t('conversions.tile.spent', {
          credits: formatCredits(summary.creditsSpent),
        })}
      />
      <StatTile
        tone="good"
        icon={<Sunrise size={14} />}
        label={t('conversions.tile.toReward')}
        value={formatNumber(toReward.completed ?? 0)}
        caption={t('conversions.tile.granted', {
          credits: formatCredits(summary.creditsGranted),
        })}
      />
      <StatTile
        icon={<ArrowRightLeft size={14} />}
        label={t('conversions.tile.applied')}
        value={formatMoneyMinor(summary.appliedValueMinor)}
        caption={t('conversions.tile.appliedCaption')}
      />
      <StatTile
        icon={<CreditCard size={14} />}
        label={t('conversions.tile.collected')}
        value={formatMoneyMinor(summary.cardCollectedMinor)}
        caption={t('conversions.tile.collectedCaption')}
      />
      <StatTile
        label={t('conversions.tile.forfeited')}
        value={formatMoneyMinor(summary.forfeitedValueMinor)}
        caption={t('conversions.tile.forfeitedCaption')}
      />
      <StatTile
        tone={stalled > 0 ? 'warn' : 'neutral'}
        icon={<CircleAlert size={14} />}
        label={t('conversions.tile.stalled')}
        value={formatNumber(stalled)}
        caption={t('conversions.tile.pending', { count: pending })}
      />
    </div>
  );
}

function ConversionRow({ conversion }: { conversion: ModeConversion }) {
  const t = useT();
  const openRecord = useOpenRecord();
  const [open, setOpen] = useState(false);
  const toPremium = conversion.direction === 'reward_to_premium';

  const partCard = toPremium && conversion.amountDueMinor > 0;
  const unpaidPremium =
    !toPremium && conversion.paymentProof?.verified === false;

  const headline = toPremium
    ? t(
        partCard
          ? 'conversions.row.toPremiumMixed'
          : 'conversions.row.toPremium',
        {
          credits: formatCredits(conversion.creditsSpent),
          paid: formatMoneyMinor(conversion.amountDueMinor),
          plan: conversion.catalogItemLabel,
        },
      )
    : t('conversions.row.toReward', {
        package: conversion.catalogItemLabel,
        paid: formatMoneyMinor(conversion.amountDueMinor),
      });

  return (
    <li className="px-4 py-3.5 sm:px-5">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              tone={toPremium ? 'brand' : 'good'}
              icon={toPremium ? <Crown size={12} /> : <Sunrise size={12} />}
            >
              {t(`conversions.direction.${conversion.direction}`)}
            </Badge>
            <Badge tone={STATUS_TONES[conversion.status]}>
              {t(`conversions.status.${conversion.status}`)}
            </Badge>
            {partCard && (
              <Badge icon={<CreditCard size={12} />}>
                {t('conversions.badge.partCard')}
              </Badge>
            )}
            {unpaidPremium && (
              <Badge tone="warn" icon={<ShieldAlert size={12} />}>
                {t('conversions.badge.unpaidPremium')}
              </Badge>
            )}
            {conversion.metadata?.settledAfterExpiry === true && (
              <Badge tone="warn">{t('conversions.lateSettlement')}</Badge>
            )}
          </div>
          <p className="mt-1.5 text-sm font-medium text-ink break-anywhere">
            {headline}
          </p>
          {conversion.failureMessage && (
            <p className="mt-1 text-xs text-danger break-anywhere">
              {conversion.failureCode && (
                <span className="font-mono">{conversion.failureCode} · </span>
              )}
              {conversion.failureMessage}
            </p>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <span className="text-xs text-ink-3">
            {formatDateTime(conversion.createdAt)}
          </span>
          <div className="flex items-center gap-2">
            <Button
              className="text-xs"
              icon={<User size={14} />}
              onClick={() => openRecord({ kind: 'user', id: conversion.userId })}
            >
              {t('conversions.openUser')}
            </Button>
            <Button
              className="text-xs"
              aria-expanded={open}
              icon={
                <ChevronDown
                  size={14}
                  className={cn('transition-transform', open && 'rotate-180')}
                />
              }
              onClick={() => setOpen((value) => !value)}
            >
              {t(open ? 'conversions.hideDetails' : 'conversions.showDetails')}
            </Button>
          </div>
        </div>
      </div>

      {open && <ConversionDetails conversion={conversion} />}
    </li>
  );
}

/**
 * The full quote and trail of one switch. Two columns from `md` up, one below:
 * the left says what was priced, the right what it touched; the trail of what
 * actually moved runs underneath, across both.
 */
function ConversionDetails({ conversion }: { conversion: ModeConversion }) {
  const t = useT();
  const toPremium = conversion.direction === 'reward_to_premium';
  const planCredits = Number(conversion.metadata?.planCredits);
  const none = '—';

  return (
    <div className="mt-3 grid gap-x-8 rounded-lg bg-raised px-3 py-2 md:grid-cols-2">
      <dl className="divide-y divide-line/60">
        <Row label={t('conversions.detail.item')}>
          {conversion.catalogItemLabel}{' '}
          <span className="font-mono text-xs text-ink-3">
            ({conversion.catalogItemId})
          </span>
        </Row>
        <Row label={t('conversions.detail.price')}>
          {formatMoneyMinor(conversion.priceMinor)}
        </Row>
        {toPremium ? (
          <>
            {planCredits > 0 && (
              <Row label={t('conversions.detail.planCredits')}>
                {formatCredits(planCredits)}
              </Row>
            )}
            <Row label={t('conversions.detail.creditsSpent')}>
              {formatCredits(conversion.creditsSpent)}
            </Row>
            <Row label={t('conversions.detail.charged')}>
              {formatMoneyMinor(conversion.amountDueMinor)}
            </Row>
            <Row label={t('conversions.detail.periodValue')}>
              {formatMoneyMinor(conversion.subscriptionValueMinor)}
            </Row>
            <Row label={t('conversions.detail.premiumEnds')}>
              {formatDateTime(conversion.premiumEndsAt)}
            </Row>
          </>
        ) : (
          <>
            <Row label={t('conversions.detail.remaining')}>
              {formatSpan(conversion.remainingSeconds)}
            </Row>
            <Row label={t('conversions.detail.remainingValue')}>
              {formatMoneyMinor(conversion.remainingValueMinor)}
            </Row>
            <Row label={t('conversions.detail.applied')}>
              {formatMoneyMinor(conversion.appliedValueMinor)}
            </Row>
            <Row label={t('conversions.detail.forfeited')}>
              {formatMoneyMinor(conversion.forfeitedValueMinor)}
            </Row>
            <Row label={t('conversions.detail.charged')}>
              {formatMoneyMinor(conversion.amountDueMinor)}
            </Row>
            <Row label={t('conversions.detail.creditsGranted')}>
              {formatCredits(conversion.creditsGranted)}
            </Row>
          </>
        )}
        <Row label={t('conversions.detail.balanceBefore')}>
          {t('conversions.detail.balanceValue', {
            balance: formatNumber(conversion.balanceBefore),
            available: formatNumber(conversion.availableBefore),
          })}
        </Row>
      </dl>

      <dl className="divide-y divide-line/60">
        <Row label={t('conversions.detail.conversionId')}>
          <IdChip id={conversion.id} />
        </Row>
        <Row label={t('conversions.detail.userId')}>
          <IdChip id={conversion.userId} />
        </Row>
        <Row label={t('conversions.detail.subscription')}>
          {conversion.subscriptionId ? (
            <IdChip id={conversion.subscriptionId} />
          ) : (
            none
          )}
        </Row>
        {!toPremium && (
          <Row label={t('conversions.detail.period')}>
            {conversion.subscriptionPeriodStart
              ? `${formatDateTime(conversion.subscriptionPeriodStart)} → ${formatDateTime(conversion.subscriptionPeriodEnd)}`
              : none}
          </Row>
        )}
        <Row label={t('conversions.detail.payment')}>
          {conversion.paymentTransactionId ? (
            <IdChip id={conversion.paymentTransactionId} />
          ) : (
            none
          )}
        </Row>
        <Row label={t('conversions.detail.clientIp')} mono>
          {conversion.clientIp ?? none}
        </Row>
        <Row label={t('conversions.detail.created')}>
          {formatDateTime(conversion.createdAt)}
        </Row>
        <Row label={t('conversions.detail.finished')}>
          {formatDateTime(conversion.completedAt ?? conversion.failedAt)}
        </Row>
      </dl>

      {!toPremium && <PaymentCheck proof={conversion.paymentProof} />}
      <ConversionTrail id={conversion.id} />
    </div>
  );
}

/**
 * Whether the Premium a switch to reward mode traded in was really paid for,
 * and by what. The credit is only ever what this block says was kept.
 */
function PaymentCheck({ proof }: { proof: PremiumPaymentProof | null }) {
  const t = useT();

  return (
    <section className="border-t border-line/60 py-2 md:col-span-2">
      <h4 className="mb-1 text-xs font-semibold tracking-wide text-ink-2 uppercase">
        {t('conversions.proof.title')}
      </h4>
      {!proof ? (
        <p className="py-1.5 text-sm text-ink-3">
          {t('conversions.proof.legacy')}
        </p>
      ) : (
        <dl className="grid gap-x-8 md:grid-cols-2">
          <Row label={t('conversions.proof.status')}>
            {proof.verified ? (
              <Badge tone="good" icon={<ShieldCheck size={12} />}>
                {t('conversions.proof.verified')}
              </Badge>
            ) : (
              <Badge tone="warn" icon={<ShieldAlert size={12} />}>
                {t('conversions.proof.unverified')}
              </Badge>
            )}
          </Row>
          <Row label={t('conversions.proof.funding')}>
            {proof.funding
              ? t(`conversions.proof.funding.${proof.funding}`)
              : '—'}
          </Row>
          <Row label={t('conversions.proof.paid')}>
            {formatMoneyMinor(proof.paidMinor)}
          </Row>
          <Row label={t('conversions.proof.recorded')}>
            {formatMoneyMinor(proof.recordedMinor)}
          </Row>
          <Row label={t('conversions.proof.evidence')}>
            {proof.paymentTransactionId ? (
              <IdChip id={proof.paymentTransactionId} />
            ) : proof.conversionId ? (
              <IdChip id={proof.conversionId} />
            ) : (
              '—'
            )}
          </Row>
          {proof.reason && (
            <Row label={t('conversions.proof.reason')}>
              <span className="break-anywhere">
                {t(`conversions.proof.reason.${proof.reason}`)}
              </span>
            </Row>
          )}
        </dl>
      )}
    </section>
  );
}

/**
 * Everything one switch moved, in order: daylights taken and given back, the
 * card checkout step by step, and how it ended. Read only once the row is
 * opened — a closed row costs nothing.
 */
function ConversionTrail({ id }: { id: string }) {
  const t = useT();
  const detail = useQuery({
    queryKey: ['conversions', 'detail', id],
    queryFn: () => api.get<ModeConversionDetail>(`/api/conversions/${id}`),
  });
  const payment = detail.data?.payment;

  return (
    <section className="border-t border-line/60 py-2 md:col-span-2">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t('conversions.trail.title')}
        </h4>
        {payment && (
          <span className="flex flex-wrap items-center gap-1.5 text-xs text-ink-2">
            {t('conversions.trail.payment')}
            <Badge>{t(`conversions.paymentStatus.${payment.status}`)}</Badge>
            <IdChip id={payment.merchantOrderId} />
          </span>
        )}
      </div>

      {detail.isPending ? (
        <Skeleton className="h-24" />
      ) : detail.isError ? (
        <ErrorNote error={detail.error} />
      ) : (
        <ol className="space-y-2.5">
          {detail.data.trail.map((entry, index) => (
            <li key={`${entry.step}-${index}`} className="flex gap-3">
              <span
                aria-hidden
                className={cn(
                  'mt-1.5 size-2 shrink-0 rounded-full',
                  DOT_TONES[TRAIL_TONES[entry.step]],
                )}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <span className="text-sm text-ink">
                    {t(`conversions.trail.${entry.step}`)}
                    {entry.credits !== null && (
                      <span className="ml-1.5 text-ink-2">
                        {formatCredits(entry.credits)}
                      </span>
                    )}
                    {entry.amountMinor !== null && entry.amountMinor > 0 && (
                      <span className="ml-1.5 text-ink-2">
                        {formatMoneyMinor(entry.amountMinor)}
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-ink-3">
                    {formatDateTime(entry.at)}
                  </span>
                </div>
                {entry.note && (
                  <p className="mt-0.5 font-mono text-xs text-ink-3 break-anywhere">
                    {entry.note}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
